/**
 * The 10:00 am research record (roadmap Y5): what the frozen quiet-day filter says about a session at
 * 10:00 New York time, what happened after 10:00, and the forward tally since the record began.
 * Research only: it is never a trade signal. Below the record sits the paper forward test of the frozen
 * bull put that the spread test (N5) ended on as a near miss: priced at 10:00 exactly as the test priced the
 * history, never traded. Below that, the D0 paper record (research/spx_1000am/FORWARD_D0.md). Above both, the three
 * records side by side with their checkpoints and the decision log's fingerprint (research/spx_1000am/FORWARD_GATES.md).
 * Data: GET /v1/ten-am (backend/ten_am.py, backend/paper_bullput.py, backend/paper_d0.py).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { Badge, Icon, Panel, StatCell, fmt, etAndLocal } from "@/sentinel";
import { TenAmCallCard } from "./TenAmCall";
import { d0Decision, d0Outcome } from "@/lib/paperD0";
import { forwardRows, gateStatus, shortHash } from "@/lib/forwardGates";

const isNum = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
const pct1 = (v) => (isNum(v) ? `${(Number(v) * 100).toFixed(1)}%` : "—");
const spct = (v, d = 2) => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v) * 100).toFixed(d)}%` : "—");
const level = (v) => (isNum(v) ? fmt(Number(v), "price") : "—");
const usd = (v) => (isNum(v) ? `${Number(v) < 0 ? "−" : ""}$${Math.abs(Number(v)).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—");
const susd = (v) => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}$${Math.abs(Number(v)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? "" : "s"}`;
const pts = (v) => (isNum(v) ? Number(v).toFixed(2) : "—");

export function useTenAm() {
  return useQuery({ queryKey: ["ten-am"], queryFn: api.tenAm, refetchInterval: 5 * 60 * 1000, retry: retryTransient });
}

function Classification({ rec }) {
  if (!rec?.classification) return null;
  const kept = rec.classification === "KEPT";
  return (
    <div className="sn-row" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }} data-testid="tenam-classification">
      <Badge tone={kept ? "brand" : undefined} icon={kept ? "Check" : "CircleMinus"}>
        {kept ? "Quiet-day filter: keeps this session" : "Quiet-day filter: excludes this session"}
      </Badge>
      <span className="sn-small sn-muted">
        {isNum(rec.vol_rank)
          ? `VIX1D closed at ${rec.vix1d_prev} the day before, in the lowest ${Math.round(rec.vol_rank * 100)}% of its past year (the filter keeps the lowest 40%).`
          : "VIX1D's rank could not be read, so the session counts as excluded."}
      </span>
    </div>
  );
}

function Outcome({ rec }) {
  if (rec.status !== "settled") {
    return <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="tenam-pending">After the close ({etAndLocal("16:35", { day: rec.day })}) the record adds what happened after 10:00.</p>;
  }
  const parts = [];
  if (rec.touch_up) parts.push(`touched +1% at ${etAndLocal(rec.touch_up_minute, { day: rec.day })}${rec.recover_up_minute ? ` (back inside at ${etAndLocal(rec.recover_up_minute, { day: rec.day })})` : " and stayed beyond it"}`);
  if (rec.touch_down) parts.push(`touched −1% at ${etAndLocal(rec.touch_down_minute, { day: rec.day })}${rec.recover_down_minute ? ` (back inside at ${etAndLocal(rec.recover_down_minute, { day: rec.day })})` : " and stayed beyond it"}`);
  const text = parts.length ? `After 10:00 the index ${parts.join("; ")}.` : `After 10:00 the index stayed within ±1% (largest move ${spct(rec.max_abs)}).`;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="tenam-outcome">
      <p className="sn-small" style={{ margin: 0 }}>{text}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="Largest rise" value={spct(rec.max_rise)} />
        <StatCell size="sm" label="Largest fall" value={spct(rec.max_fall)} />
        <StatCell size="sm" label="Official close" value={level(rec.settlement)} sub={isNum(rec.settlement_ret) ? `${spct(rec.settlement_ret)} from 10:00` : null} />
        <StatCell size="sm" label="Last print" value={level(rec.close_print)} sub="can differ from the official close" />
      </div>
    </div>
  );
}

function DayRecord({ rec, day }) {
  if (!rec) {
    return (
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="tenam-none">
        No record for {day || "this session"}. The record is captured at {etAndLocal("10:01:15", { day })}, once the 10:00 minute has closed,
        on every trading day from the day it went live.
      </p>
    );
  }
  if (!isNum(rec.ref)) {
    return <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="tenam-nolevel">No 10:00 level was recorded for {rec.day}{rec.errors?.length ? `: ${rec.errors.join("; ")}` : ""}.</p>;
  }
  const after = rec.events_after || [];
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)" }} data-testid="tenam-day">
      <Classification rec={rec} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="SPX at 10:00" value={level(rec.ref)} sub={rec.day} data-testid="tenam-ref" />
        <StatCell size="sm" label="Open to 10:00" value={spct(rec.open_to_entry)} />
        <StatCell size="sm" label="Overnight gap" value={spct(rec.overnight_gap)} sub={isNum(rec.prior_close) ? `prior close ${level(rec.prior_close)}` : null} />
        <StatCell size="sm" label="VIX at prior close" value={isNum(rec.vix_prev) ? Number(rec.vix_prev).toFixed(2) : "—"} />
      </div>
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="tenam-releases">
        {after.length
          ? <>Still due after 10:00: {after.map((e) => `${etAndLocal(e.time_et, { day: rec.day })} ${e.event}`).join(" · ")}.</>
          : "No major release or Fed decision was scheduled after 10:00."}
        {rec.fomc_day ? " This is an FOMC day." : ""}
      </p>
      <Outcome rec={rec} />
    </div>
  );
}

function ForwardTable({ data }) {
  const f = data.forward || {};
  const v = data.validation_2024 || {};
  const row = (label, r, testId) => (
    <tr data-testid={testId}>
      <td>{label}</td>
      <td className="num">{r?.n ?? 0}</td>
      <td className="num">{r?.touched ?? 0}</td>
      <td className="num" style={{ color: "var(--text-primary)" }}>{pct1(r?.rate)}</td>
      <td className="num sn-muted">{isNum(r?.lo) ? `${pct1(r.lo)} to ${pct1(r.hi)}` : "—"}</td>
    </tr>
  );
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="tenam-forward">
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label="Forward record of the quiet-day filter">
          <thead><tr><th>Since the record began{f.since ? ` (${f.since})` : ""}</th><th className="r">Sessions</th><th className="r">Touched ±1%</th><th className="r">Rate</th><th className="r">95% range</th></tr></thead>
          <tbody>
            {row("All sessions", f.all, "tenam-fwd-all")}
            {row("Kept by the filter", f.kept, "tenam-fwd-kept")}
            {row("Excluded", f.excluded, "tenam-fwd-excluded")}
          </tbody>
        </table>
      </div>
      <p className="sn-small sn-faint" style={{ margin: 0 }}>
        For comparison, the one look at 2024: {v.kept} of {v.sessions} sessions kept, touching ±1% after 10:00 on {pct1(v.touch_kept)} of
        them against {pct1(v.touch_all)} of all sessions and {pct1(v.touch_excluded)} of the excluded. A small forward sample has a wide range;
        it is read only when it is large.
      </p>
    </div>
  );
}

function PaperOutcome({ t }) {
  if (!t || t.status !== "settled") return <FlagLine f={t?.flags} />;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="paper-outcome">
        {t.touch_short_minute
          ? `The index touched the ${t.short} strike at ${etAndLocal(t.touch_short_minute, { day: t.day })}${t.settled_itm ? " and settled below it." : ", then recovered and the spread expired worthless."}`
          : `The index never touched the ${t.short} strike after 10:00.`}
      </p>
      <FlagLine f={t.flags} />
    </div>
  );
}

function FlagLine({ f }) {
  if (!f || f.error) return null;
  const z = isNum(f.lead_z) ? `${Number(f.lead_z) >= 0 ? "+" : "−"}${Math.abs(Number(f.lead_z)).toFixed(1)} standard deviations` : "no standardised surprise";
  return (
    <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="paper-flags">
      Research flags (recorded, never used to decide): {f.event_day ? `event day, ${f.lead} surprise ${z}` : "not an event day"}
      {f.major_surprise ? "; a major surprise, which the N6 filter would have skipped." : "."}
    </p>
  );
}

function OrdinaryRecord({ r }) {
  if (!r) return null;
  return (
    <p className="sn-small" style={{ margin: 0 }} data-testid="paper-ordinary">
      <b>Ordinary-day record</b> (no release before or at 10:00, not FOMC; its own test, judged at {r.judge_at} trades):{" "}
      {r.trades ? <>{r.trades} of {r.judge_at} settled, {susd(r.mean)} a trade{r.ci95 ? ` (95% range ${susd(r.ci95[0])} to ${susd(r.ci95[1])})` : ""}, {pct1(r.win_rate)} won.</> : "no ordinary-day trade has settled yet."}
    </p>
  );
}

function FlagSplit({ fl }) {
  if (!fl) return null;
  const rows = [["Without a major surprise", fl.major_surprise?.without], ["With a major surprise", fl.major_surprise?.with],
    ["Ordinary days", fl.event_day?.ordinary], ["Event days", fl.event_day?.event]];
  if (!rows.some(([, r]) => r?.trades)) return null;
  return (
    <div className="sn-table-wrap" data-testid="paper-flag-split">
      <table className="sn-table" aria-label="Paper record by research flag">
        <thead><tr><th>Research flags (read at 500 trades)</th><th className="r">Trades</th><th className="r">Mean</th><th className="r">Won</th></tr></thead>
        <tbody>
          {rows.map(([label, r]) => (
            <tr key={label}><td>{label}</td><td className="num">{r?.trades ?? 0}</td><td className="num">{susd(r?.mean)}</td><td className="num">{pct1(r?.win_rate)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaperTally({ p }) {
  const s = p.summary || {};
  const f = p.final_period || {};
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="paper-tally">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="Paper trades settled" value={`${s.settled ?? 0} of ${s.judge_at ?? 500}`}
          sub={s.since ? `since ${s.since}` : "starts with the next session"} data-testid="paper-count" />
        <StatCell size="sm" label="Mean per trade" value={susd(s.mean)}
          sub={s.ci95 ? `95% range ${susd(s.ci95[0])} to ${susd(s.ci95[1])}` : null} />
        <StatCell size="sm" label="Win rate" value={pct1(s.win_rate)} sub={isNum(s.total) ? `total ${susd(s.total)}` : null} />
        <StatCell size="sm" label="Deepest drawdown" value={usd(s.max_drawdown)}
          sub={s.worst_month ? `worst month ${s.worst_month.month}, ${susd(s.worst_month.net)}` : null} />
      </div>
      <OrdinaryRecord r={p.flags?.ordinary_record} />
      <FlagSplit fl={p.flags} />
      {s.review_flag && (
        <p className="sn-small" style={{ margin: 0, color: "var(--price-down)" }} data-testid="paper-review">
          Review flag: the paper record has fallen more than $10,000 below its peak, or lost over $2,000 in each of three months in a
          row. The rule is not changed; the owner reviews it.
        </p>
      )}
      <p className="sn-small sn-faint" style={{ margin: 0 }}>
        Judged once, at {s.judge_at ?? 500} settled trades: a pass needs the 95% range for the mean above zero. For comparison, the
        final test period (2025 to 2026): {f.trades} trades, {susd(f.mean)} a trade, range {susd(f.ci95?.[0])} to {susd(f.ci95?.[1])},
        a near miss. A small paper sample has a wide range and is not read before the count is reached.
      </p>
    </div>
  );
}

function ForwardGates({ data }) {
  const rows = forwardRows(data);
  if (!rows.length) return null;
  const log = data.paper_log || {};
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)", borderTop: "1px solid var(--border-hairline)", paddingTop: "var(--space-3)" }} data-testid="forward-gates">
      <div className="sn-row" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
        <b className="sn-small">Forward tests</b>
        <Badge icon="FlaskConical">Paper only · checkpoints can reject, never pass early</Badge>
      </div>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label="The three paper records and their checkpoints">
          <thead><tr><th>Record</th><th className="r">Settled</th><th className="r">Mean per trade</th><th className="r">95% range</th><th className="r">Total</th><th className="r">Drawdown</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const st = gateStatus(r.gates);
              return (
                <tr key={r.key} data-testid={`forward-${r.key}`}>
                  <td>{r.name}</td>
                  <td className="num">{r.settled} of {r.target}</td>
                  <td className="num">{susd(r.mean)}</td>
                  <td className="num sn-muted">{r.ci95 ? `${susd(r.ci95[0])} to ${susd(r.ci95[1])}` : "—"}</td>
                  <td className="num">{susd(r.total)}</td>
                  <td className="num">{usd(r.drawdown)}</td>
                  <td style={st.tone === "down" ? { color: "var(--price-down)" } : undefined}>{st.label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="forward-log">
        At a quarter, a half and three quarters of its target, each record is rejected if it is losing money with 95% confidence;
        otherwise it continues. A pass is decided only at the target. Every decision, quote, credit and settlement is in an
        append-only log ({log.entries ?? 0} entries, latest fingerprint {shortHash(log.head)}); changing any past entry would
        break every fingerprint after it. Settlements are checked against Cboe's official close each morning and evening.
      </p>
    </div>
  );
}

function PaperD0({ p, day }) {
  const s = p.summary || {};
  const h = p.n8_history || {};
  const t = (p.recent || []).find((x) => x.day === day) || null;
  const outcome = d0Outcome(t);
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)", borderTop: "1px solid var(--border-hairline)", paddingTop: "var(--space-3)" }} data-testid="paper-d0">
      <div className="sn-row" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
        <b className="sn-small">D0 paper record</b>
        <Badge icon="FlaskConical">Paper · separate test</Badge>
      </div>
      <p className="sn-small" style={{ margin: 0 }} data-testid="paper-d0-decision">{d0Decision(t)}</p>
      {outcome && <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="paper-d0-outcome">{outcome}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="D0 trades settled" value={`${s.settled ?? 0} of ${s.judge_at ?? 150}`}
          sub={s.since ? `since ${s.since}` : "starts with the next session"} data-testid="paper-d0-count" />
        <StatCell size="sm" label="Mean per trade" value={susd(s.mean)}
          sub={s.ci95 ? `95% range ${susd(s.ci95[0])} to ${susd(s.ci95[1])}` : null} />
        <StatCell size="sm" label="Win rate" value={pct1(s.win_rate)}
          sub={s.by_side ? `${plural(s.by_side.put ?? 0, "bull put")}, ${plural(s.by_side.call ?? 0, "bear call")}` : null} />
        <StatCell size="sm" label="Deepest drawdown" value={usd(s.max_drawdown)}
          sub={isNum(s.no_trade) ? `${plural(s.no_trade, "session")} without a trade` : null} />
      </div>
      {s.review_flag && (
        <p className="sn-small" style={{ margin: 0, color: "var(--price-down)" }} data-testid="paper-d0-review">
          Review flag: D0 has fallen more than $10,000 below its peak, or lost over $2,000 in each of three months in a row. The rule is
          not changed; the owner reviews it.
        </p>
      )}
      <p className="sn-small sn-faint" style={{ margin: 0 }}>
        At 10:00, D0 prices the bull put and the bear call at six distances (0.3% to 1.2%) from the real quotes. It values each
        one against past moves to the close, scaled by the day's VIX1D, and trades the best one only if it expects at least 1% on its
        maximum loss. It is judged once at {s.judge_at ?? 150} settled trades. In the earlier study it made {susd(h.mean)} a trade over{" "}
        {h.trades} trades, but that was found after the fact, so only these new sessions count.
      </p>
    </div>
  );
}

/**
 * `day`: the session to show (the desk's selected date); defaults to today's record.
 * `compact`: the day only, for the desk and the phone card; otherwise the forward tally as well.
 */
export function TenAmRecordPanel({ day, compact = false }) {
  const q = useTenAm();
  return (
    <Panel title="10:00 am call and research record" icon="Timer" data-testid="tenam-panel"
      meta={<Badge icon="FlaskConical">Research · not a signal</Badge>}>
      <QueryBoundary query={q} loading={2} what="the 10:00 record" errorTestId="tenam-error" isEmpty={() => false}>
        {(data) => {
          const rec = day ? (data.recent || []).find((r) => r.day === day) || (data.today?.day === day ? data.today : null) : data.today || (data.recent || [])[0];
          return (
            <div className="sn-stack" style={{ gap: "var(--space-4)" }}>
              {(() => {
                const t = data.paper_bullput ? (data.paper_bullput.recent || []).find((x) => x.day === (rec?.day || day)) || null : null;
                return <TenAmCallCard rec={rec} t={t} paper={data.paper_bullput} />;
              })()}
              <div style={{ borderTop: "1px solid var(--border-hairline)", paddingTop: "var(--space-3)" }}>
                <DayRecord rec={rec} day={day} />
              </div>
              {!compact && <ForwardGates data={data} />}
              {data.paper_bullput && !compact && (
                <div className="sn-stack" style={{ gap: "var(--space-3)", borderTop: "1px solid var(--border-hairline)", paddingTop: "var(--space-3)" }} data-testid="paper-section">
                  <PaperOutcome t={(data.paper_bullput.recent || []).find((t) => t.day === (rec?.day || day)) || null} />
                  <PaperTally p={data.paper_bullput} />
                </div>
              )}
              {data.paper_d0 && !compact && <PaperD0 p={data.paper_d0} day={rec?.day || day} />}
              {!compact && <ForwardTable data={data} />}
              <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="tenam-note">
                <Icon name="Info" size={12} style={{ marginRight: 4, verticalAlign: "-1px" }} />
                The filter is frozen from 2021 to 2023 and passed its one test on 2024 ({data.filter}). It forecasts movement, not
                profit. The spread test on real 10:00 quotes ended as a near miss: the bull put 0.9% below the 10:00 level made a small
                average that could not be told apart from zero, so it now runs as a paper forward test. Nothing here is traded.
              </p>
            </div>
          );
        }}
      </QueryBoundary>
    </Panel>
  );
}
